import React, { useMemo, useState } from "react";
import Box from "@mui/material/Box";
import ProductionForecastTable from "./ProductionForecastTable";
import ProductionForecastFixedTable from "./ProductionForecastFixedTable";
import ProductionForecastCharts from "./ProductionForecastCharts";
import { get15MinIntervalsForDate } from "helpers/timeIntervals";

const ProductionForecastData = ({
  setSnackbar,
  forecastGroup,
  productionParks,
  filters,
}) => {
  const [allTotals, setAllTotals] = useState<Record<string, number[]> | null>(
    null,
  );

  const periods = get15MinIntervalsForDate(forecastGroup?.forecastDate);

  const totalForecast = useMemo(() => {
    if (!allTotals || !forecastGroup?.forecasts) return [];

    const allowedIds = forecastGroup.forecasts.map((forecast) => forecast.id);

    const forecastArrays = Object.entries(allTotals)
      .filter(
        ([key, value]) =>
          allowedIds.includes(Number(key)) && Array.isArray(value),
      )
      .map(([, value]) => value);

    if (forecastArrays.length === 0) return [];

    const length = forecastArrays[0].length;

    // Ensure all forecast arrays are the same length
    if (!forecastArrays.every((arr) => arr.length === length)) {
      console.warn("Forecast arrays have mismatched lengths");
      return [];
    }

    const summedForecast = Array(length).fill(0);

    forecastArrays.forEach((arr) => {
      arr.forEach((val, idx) => {
        summedForecast[idx] += val;
      });
    });

    return summedForecast;
  }, [allTotals]);

  const handleForecastCalculated = (forecastId: string, values: number[]) => {
    setAllTotals((prev) => ({ ...(prev || {}), [forecastId]: values }));
  };

  return (
    <Box display="flex" justifyContent="flex-start" alignItems="flex-start">
      <Box
        display="flex"
        justifyContent="flex-start"
        alignItems="flex-start"
        width="100%"
        style={{ overflow: "scroll", overflowY: "hidden" }}
      >
        <ProductionForecastFixedTable
          totalForecast={totalForecast}
          forecastDate={forecastGroup?.forecastDate}
        />
        {forecastGroup?.forecasts.map((forecast) => {
          return (
            <ProductionForecastTable
              productionForecast={forecast}
              productionParks={productionParks}
              setSnackbar={setSnackbar}
              onCalculated={handleForecastCalculated}
              calculatedValues={allTotals?.[forecast.id]}
              key={forecast.id}
              filters={filters}
            />
          );
        })}
      </Box>
      <ProductionForecastCharts
        xAxisLength={periods.length}
        totalValues={forecastGroup?.totalValues}
        filters={filters}
      />
    </Box>
  );
};

export default ProductionForecastData;
